import { BaseSelectMenuOption } from "@/types/components/selector.types";
import { Stack } from "@mui/material";
import { debounce, isEqual } from "lodash";
import { useCallback, useMemo, useRef, useState } from "react";
import { Dialog } from "../../components/dialogs/Dialog";
import { SelectMenu } from "../../components/SelectMenu";
import { MultiAttributeSelector } from "../../components/selectors/MultiAttributeSelector";
import { useAppQuery } from "../../hooks/useAppQuery";
import { useLocale } from "../../providers/LocaleProvider";

export interface AttributeCategory {
  id: string;
  name: string;
  createdDate: string;
  lastUpdatedDate: string;
}

interface AttributeCategoryResponse {
  competencyCategories: AttributeCategory[];
}

export const YES_NO_OPTIONS = {
  ALL: {
    value: "all",
    label: "Show All"
  },
  YES: {
    value: true,
    label: "Yes"
  },
  NO: {
    value: false,
    label: "No"
  }
};

interface SettingStandardsLibaryFilterCompetenciesProps {
  onClose: () => void;
  onSave: (filters: {
    categories: AttributeCategory[];
    expiry: string | boolean | null;
    training: string | boolean | null;
  }) => void;
  theme: any;
  categories: any[];
  isLoading: boolean;
  open: boolean;
  initialSelectedCategories?: AttributeCategory[];
  initialExpiryFilter?: (typeof YES_NO_OPTIONS)[keyof typeof YES_NO_OPTIONS];
  initialTrainingFilter?: (typeof YES_NO_OPTIONS)[keyof typeof YES_NO_OPTIONS];
}

export const SettingStandardsLibaryFilterCompetencies = (
  props: SettingStandardsLibaryFilterCompetenciesProps
) => {
  const { translate } = useLocale();
  const {
    onClose,
    onSave,
    theme,
    open,
    initialSelectedCategories = [],
    initialExpiryFilter = YES_NO_OPTIONS.ALL,
    initialTrainingFilter = YES_NO_OPTIONS.ALL
  } = props;

  // Localized yes/no options
  const localizedYesNoOptions = {
    ALL: { value: "all", label: translate("common.showAll") },
    YES: { value: true, label: translate("common.yes") },
    NO: { value: false, label: translate("common.no") }
  };

  const initialValuesRef = useRef({
    id: "filter-initial-values",
    name: "Filter Initial Values",
    categories: initialSelectedCategories,
    expiry: initialExpiryFilter,
    training: initialTrainingFilter
  });

  const [selectedCategories, setSelectedCategories] =
    useState<AttributeCategory[]>(initialSelectedCategories);
  const [expiryFilter, setExpiryFilter] = useState(initialExpiryFilter);
  const [trainingFilter, setTrainingFilter] = useState(initialTrainingFilter);
  const [searchText, setSearchText] = useState("");

  const { data: categoriesData, isLoading: isLoadingCategories } =
    useAppQuery<AttributeCategoryResponse>("/StandardLibrary/competencies/categories", {
      enabled: open,
      staleTime: 60 * 1000,
      gcTime: 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false
    });

  const handleAttributesChange = (e: any, selected: any) => {
    setSelectedCategories(selected);
  };

  const handleExpiryFilterChange = (e: any) => {
    setExpiryFilter(e.target.value);
  };

  const handleTrainingFilterChange = (e: any) => {
    setTrainingFilter(e.target.value);
  };

  const handleSave = () => {
    if (onSave)
      onSave({
        expiry: expiryFilter.value !== "all" ? expiryFilter.value : null,
        training: trainingFilter.value !== "all" ? trainingFilter.value : null,
        categories: selectedCategories
      });
  };

  const handleSearchCategories = useCallback(
    debounce((searchText: string) => {
      setSearchText(searchText);
    }, 500),
    []
  );

  const categoriesFiltered = useMemo(() => {
    return categoriesData?.competencyCategories || [];
  }, [categoriesData]);

  return (
    <Dialog
      open={open}
      theme={theme}
      onClose={onClose}
      fullWidth={true}
      maxWidth='sm'
      title={translate("team.filters")}
      actions={[
        {
          label: translate("common.apply"),
          primary: true,
          disabled:
            isEqual(selectedCategories, initialValuesRef.current.categories) &&
            expiryFilter.value === initialValuesRef.current.expiry.value &&
            trainingFilter.value === initialValuesRef.current.training.value,
          onClick: handleSave
        },
        {
          label: translate("common.cancel"),
          onClick: onClose
        }
      ]}
    >
      <Stack direction='column' spacing={2}>
        <div>{translate("entities.categories")}</div>

        <MultiAttributeSelector
          label={null}
          items={categoriesFiltered}
          values={selectedCategories}
          getItemId={(attr: any) => attr.id}
          onChange={handleAttributesChange}
          hasNewItem={false}
          isLoading={isLoadingCategories}
          placeHolder={translate("placeholders.searchCategories")}
          onSearch={handleSearchCategories}
          translate={translate}
        />
      </Stack>

      <Stack direction='column' spacing={2} sx={{ marginBottom: "1rem" }}>
        <div>{translate("dialogs.expires")}</div>

        <SelectMenu<BaseSelectMenuOption>
          className='expiry-filter-menu'
          value={expiryFilter}
          firstValueDefault={true}
          dense={true}
          disabled={false}
          items={Object.entries(localizedYesNoOptions)}
          onChange={handleExpiryFilterChange}
          sx={{
            width: "100%"
          }}
        />
      </Stack>

      <Stack direction='column' spacing={2}>
        <div>{translate("entities.training")}</div>

        <SelectMenu
          className='training-filter-menu'
          value={trainingFilter}
          firstValueDefault={true}
          dense={true}
          disabled={false}
          items={Object.entries(localizedYesNoOptions)}
          onChange={handleTrainingFilterChange}
          sx={{
            width: "100%"
          }}
        />
      </Stack>
    </Dialog>
  );
};
